import { useDynamicData, useMatchSmallViewport } from '@avensia/excite';
import { TestIdProps } from '@avensia/test-automation';
import { styled } from '@glitz/react';
import { type DirtyStyle } from '@glitz/react/types/styled/use-glitz';
import * as React from 'react';
import Image from 'webapp/components/image';
import { calcBundleDiscount } from 'webapp/shared/calc-bundle-discount';
import { sharedSpacing, white } from 'webapp/shared/sharedTheme';
import { NnTestIdProps } from 'webapp/shared/test-ids';
import { createMaxSmallViewportDecorator } from 'webapp/viewport';

type Props = {
  badge?: NordicNest.Api.DiscountBadgeApiModel;
  discount?: string;
  productBadgesContext?: NordicNest.Api.ProductBadgesContext;
  newsBadge?: NordicNest.Api.NewsBadgeApiModel;
  align?: 'left' | 'right';
  priceModel?: NordicNest.Api.PriceApiModel;
  isBundle?: boolean;
};

type DiscountBadgeProps = {
  badge?: NordicNest.Api.DiscountBadgeApiModel;
  discount?: string;
  newsBadge?: NordicNest.Api.NewsBadgeApiModel;
  align?: 'left' | 'right';
  priceModel?: NordicNest.Api.PriceApiModel;
  isBundle?: boolean;
};

type ProductBadgeProps = {
  productBadgesContext: NordicNest.Api.ProductBadgesContext;
  align?: 'left' | 'right';
};

export default React.memo(function Badge({
  badge,
  newsBadge,
  discount,
  productBadgesContext,
  align,
  priceModel,
  isBundle = false,
}: Props) {
  return badge || newsBadge ? (
    <DiscountBadge
      badge={badge}
      newsBadge={newsBadge}
      discount={discount}
      align={align}
      priceModel={priceModel}
      isBundle={isBundle}
    />
  ) : productBadgesContext ? (
    <ProductBadge productBadgesContext={productBadgesContext} align={align} />
  ) : null;
});

function DiscountBadge({
  badge,
  newsBadge,
  discount,
  align = 'left',
  priceModel,
  isBundle = false,
}: DiscountBadgeProps) {
  const [bundleDiscount, setBundleDiscount] = React.useState(calcBundleDiscount(isBundle, priceModel));

  React.useEffect(() => {
    setBundleDiscount(calcBundleDiscount(isBundle, priceModel));
  }, [priceModel, isBundle]);

  if (!badge?.text) {
    return newsBadge ? (
      <Base {...TestIdProps.NewProductFlagPDP} css={alignDecorator(align)}>
        <Text
          css={{
            backgroundColor: t => newsBadge.bgColor || t.monochrome.color,
            color: t => newsBadge.textColor || t.monochrome.text,
          }}
        >
          {newsBadge.text}
        </Text>
      </Base>
    ) : null;
  }

  return (
    <Base css={alignDecorator(align)}>
      <Text
        {...NnTestIdProps.DiscountBadge}
        css={{
          backgroundColor: t => badge.bgColor || t.monochrome.color,
          color: t => badge.textColor || t.monochrome.text,
        }}
      >
        {badge.text}
      </Text>
      {((discount && !isBundle) || bundleDiscount) && (
        <Discount
          css={{
            color: t => badge.bgColor || t.monochrome.color,
          }}
        >
          {isBundle ? bundleDiscount : discount}
        </Discount>
      )}
    </Base>
  );
}

function ProductBadge({ productBadgesContext, align = 'right' }: ProductBadgeProps) {
  const isSmallViewport = useMatchSmallViewport();
  const [productBadge, setProductBadge] = React.useState<NordicNest.Api.ProductBadgeApiModel | undefined>(undefined);
  const [productBadgesApiModel] = useDynamicData<NordicNest.Api.ProductBadgesApiModel>({
    context: productBadgesContext,
  });

  React.useEffect(() => {
    if (productBadgesApiModel && productBadgesApiModel.productBadges.length > 0) {
      const firstPriorityBadge = getFirstPriorityBadge(productBadgesApiModel, isSmallViewport);
      setProductBadge(firstPriorityBadge);
    }
  }, [productBadgesApiModel, isSmallViewport]);

  const iconCss: DirtyStyle = {};
  if (productBadge?.icon) {
    iconCss.maxHeight = productBadge.iconHeight === 'single' ? (isSmallViewport ? 22 : 25) : isSmallViewport ? 44 : 56;

    iconCss.marginTop = isSmallViewport ? sharedSpacing.xs : sharedSpacing.s;
    iconCss.marginLeft = isSmallViewport ? sharedSpacing.xs : sharedSpacing.s;
    iconCss.marginRight = isSmallViewport ? sharedSpacing.xs : sharedSpacing.s;
  }

  return productBadge ? (
    <Base css={alignDecorator(align)}>
      {productBadge.icon ? (
        <Icon image={productBadge.icon} preset={'tiny'} css={iconCss} />
      ) : (
        <Text
          css={{
            backgroundColor: t => productBadge.bgColor || t.monochrome.color,
            color: t => productBadge.textColor || t.monochrome.text,
          }}
        >
          {productBadge.text}
        </Text>
      )}
    </Base>
  ) : null;
}

function getFirstPriorityBadge(
  productBadgesApiModel: NordicNest.Api.ProductBadgesApiModel,
  isSmallViewport: boolean,
): NordicNest.Api.ProductBadgeApiModel | undefined {
  productBadgesApiModel.productBadges.sort((a, b) => a.priority - b.priority);

  for (let i = 0; i < productBadgesApiModel.productBadges.length; i++) {
    if (shouldRenderOnDevice(productBadgesApiModel.productBadges[i], isSmallViewport)) {
      return productBadgesApiModel.productBadges[i];
    }
  }

  return;
}

function shouldRenderOnDevice(
  productBadge: NordicNest.Api.ProductBadgeApiModel | undefined,
  isSmallViewport: boolean,
): boolean {
  switch (productBadge?.displayConditions?.deviceVisibility) {
    case 'all':
      return true;
    case 'mobile':
      return isSmallViewport;
    case 'desktop':
      return !isSmallViewport;
    default:
      return true;
  }
}

function alignDecorator(align: string) {
  return align === 'left'
    ? styled(createMaxSmallViewportDecorator({ left: 0 }), {
        left: 0,
        alignItems: 'flex-start',
        transform: 'translate3d(0,0,0)',
      })
    : styled(createMaxSmallViewportDecorator({ right: 0 }), {
        right: 0,
        alignItems: 'flex-end',
        transform: 'translate3d(0,0,0)',
      });
}

const Base = styled.div({
  pointerEvents: 'none',
  position: 'absolute',
  top: 0,
  display: 'flex',
  flexDirection: 'column',
  transform: 'translate3d(0,0,0)', //Needed to fix rendering bug in iOS
});

const BadgeBase = styled.div(
  createMaxSmallViewportDecorator({
    height: 22,
  }),
  {
    fontSize: t => t.sharedFontSize.xxs,
    padding: { x: t => t.sharedSpacing.xs },
    height: 24,
    display: 'flex',
    alignItems: 'center',
  },
);

const Text = styled(BadgeBase, {
  borderRadius: t => t.radius.secondary,
});

const Discount = styled(BadgeBase, {
  backgroundColor: white,
  borderRadius: t => t.radius.secondary,
});

const Icon = styled(Image, {
  width: 'auto',
});
